import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { emergencyOverrideService } from '../services/emergencyOverrideService'
import { useAuth } from '../contexts/AuthContext'
import { formatDate } from '../utils/locale'
import './OverrideRequestList.css'

const FIELD_LABELS: Record<string, string> = {
  medications: 'Medications',
  conditions: 'Conditions',
  'sensitive-documents': 'Sensitive Documents',
  'vital-signs': 'Vital Signs',
  'lab-results': 'Lab Results',
  allergies: 'Allergies',
  'family-history': 'Family History',
}

export function OverrideRequestList() {
  const { canOverrideAccess } = useAuth()
  const queryClient = useQueryClient()

  const { data: pendingRequests, isLoading } = useQuery({
    queryKey: ['emergency-override', 'pending'],
    queryFn: () => emergencyOverrideService.getPending(),
    enabled: canOverrideAccess(),
  })

  const approveMutation = useMutation({
    mutationFn: ({ overrideId, expiresInHours }: { overrideId: string; expiresInHours?: number }) =>
      emergencyOverrideService.approve({ overrideId, expiresInHours }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emergency-override', 'pending'] })
      queryClient.invalidateQueries({ queryKey: ['emergency-override'] })
    },
  })

  const rejectMutation = useMutation({
    mutationFn: ({ overrideId, rejectionReason }: { overrideId: string; rejectionReason?: string }) =>
      emergencyOverrideService.reject({ overrideId, rejectionReason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emergency-override', 'pending'] })
    },
  })

  const handleApprove = async (overrideId: string) => {
    const expiresInHours = window.prompt(
      'Enter expiration time in hours (default: 24):',
      '24'
    )
    if (expiresInHours === null) return

    const hours = parseInt(expiresInHours, 10)
    if (isNaN(hours) || hours <= 0) {
      alert('Please enter a valid number of hours')
      return
    }

    try {
      await approveMutation.mutateAsync({ overrideId, expiresInHours: hours })
      alert('Override request approved')
    } catch (error: any) {
      alert(error?.response?.data?.message || error?.message || 'Failed to approve override')
    }
  }

  const handleReject = async (overrideId: string) => {
    const rejectionReason = window.prompt(
      'Enter rejection reason (optional):',
      ''
    )
    if (rejectionReason === null) return

    try {
      await rejectMutation.mutateAsync({ overrideId, rejectionReason: rejectionReason || undefined })
      alert('Override request rejected')
    } catch (error: any) {
      alert(error?.response?.data?.message || error?.message || 'Failed to reject override')
    }
  }

  if (!canOverrideAccess()) {
    return (
      <div className="override-request-list-empty">
        <p>You do not have permission to view override requests.</p>
      </div>
    )
  }

  if (isLoading) {
    return <div className="override-request-list-loading">Loading override requests...</div>
  }

  if (!pendingRequests || pendingRequests.length === 0) {
    return (
      <div className="override-request-list-empty">
        <p>No pending override requests.</p>
      </div>
    )
  }

  return (
    <div className="override-request-list">
      <div className="override-request-list-header">
        <h2>Pending Emergency Access Requests</h2>
        <p className="subtitle">
          Review and approve or reject emergency access requests to restricted patient information.
        </p>
      </div>

      <div className="override-requests">
        {pendingRequests.map((request) => (
          <div key={request.id} className="override-request-item">
            <div className="override-request-header">
              <div className="override-request-info">
                <h3>Emergency Access Request</h3>
                <div className="override-request-meta">
                  <span className="meta-item">
                    <strong>Requested by:</strong> {request.userId}
                  </span>
                  <span className="meta-item">
                    <strong>Patient ID:</strong> {request.patientId}
                  </span>
                  <span className="meta-item">
                    <strong>Requested:</strong> {formatDate(request.requestedAt)}
                  </span>
                </div>
              </div>
              <div className="override-request-status">
                <span className={`badge badge-${request.status}`}>
                  {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                </span>
              </div>
            </div>

            <div className="override-request-details">
              <div className="detail-group">
                <label>Requested Fields:</label>
                <ul className="fields-list">
                  {request.fields.map((field) => (
                    <li key={field}>{FIELD_LABELS[field] || field}</li>
                  ))}
                </ul>
              </div>

              <div className="detail-group">
                <label>Reason:</label>
                <p className="reason-text">{request.reason}</p>
              </div>
            </div>

            <div className="override-request-actions">
              <button
                className="btn-primary btn-small"
                onClick={() => handleApprove(request.id)}
                disabled={approveMutation.isPending || rejectMutation.isPending}
              >
                {approveMutation.isPending ? 'Approving...' : 'Approve'}
              </button>
              <button
                className="btn-secondary btn-small"
                onClick={() => handleReject(request.id)}
                disabled={approveMutation.isPending || rejectMutation.isPending}
              >
                {rejectMutation.isPending ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default OverrideRequestList
